import {
  initializePaddle as initializePaddleFromPackage,
  getPaddleInstance,
  CheckoutEventNames,
  PaddleEventData,
  CheckoutSettings,
  CheckoutOpenOptions,
  CheckoutOpenLineItem,
  AvailablePaymentMethod,
  CheckoutEventsData,
} from "@paddle/paddle-js";
import { useRouter } from "vue-router/composables";
import { isEmpty, isNil, isObject } from "lodash-es";
import axios from "axios";

import useLegacyStore from "@Composables/useLegacyStore";
import useNotify from "@Composables/useNotify";

import XInvoiceStore from "@Modules/billing/Store/XInvoiceStore";
import XPaymentMethodStore from "@Modules/billing/Store/XPaymentMethodStore";

import BILLING_TYPE from "@Constants/billing-type";
import { IS_PADDLE_LIVE, PADDLE_CLIENT_SECRET, PADDLE_MOCK } from "@Constants/env";
import PADDLE_TRANSACTION_TYPE from "@Constants/paddle-transaction-type";
import { logError, logInfo } from "@Helpers/logger";

//
// Types

/**
 * Customer auth info returned from our backend's Paddle checkout initialization.
 * Extends Paddle's customer concept with our auth_token.
 */
export interface PaddleCustomerAuth {
  id: string
  auth_token: string
}

/**
 * Price item for subscription/product checkout.
 * Maps to Paddle's CheckoutOpenLineItem with snake_case from our backend.
 */
export interface PaddlePriceItem {
  price_id: string
  quantity?: number
}

/**
 * Fields every checkout payload shares, carrying the compact payload's optionality
 * because the slim backend response only guarantees `transaction_id`.
 */
export interface PaddleBaseCheckoutContent {
  transaction_id: string
  locale?: string
  allow_discounts?: boolean
  payment_methods?: AvailablePaymentMethod[] | null
  payment_method?: string | null
  discount_code?: string
  customer?: PaddleCustomerAuth
  custom_data?: Record<string, unknown>
}

/**
 * Full checkout content returned from backend checkout API.
 * Combines data from:
 * - BillingService::checkoutInit() - billing_type, locale, order_id
 * - Paddle/Checkout::initData() - transaction_id, customer, success_url, etc.
 * - Frontend - organization slug when in org context
 */
export interface PaddleCheckoutContent extends PaddleBaseCheckoutContent {
  // Guaranteed by the full checkout-init payload
  locale: string
  allow_discounts: boolean
  payment_methods: AvailablePaymentMethod[] | null
  payment_method: string | null
  customer: PaddleCustomerAuth

  // From BillingService::checkoutInit()
  billing_type: string
  order_id: number

  // From Paddle/Checkout::initData()
  success_url: string
  cancel_url: string

  // Added by frontend when in org context
  organization?: string

  // Optional fields
  prices?: PaddlePriceItem[]
}

/**
 * Compact checkout content for invoice payments and payment method updates.
 * Subset of PaddleCheckoutContent with fewer required fields.
 */
export interface PaddleCompactCheckoutContent extends PaddleBaseCheckoutContent {}

/**
 * The checkout options shared by every `Paddle.Checkout.open` call.
 */
interface CommonCheckoutData {
  settings: CheckoutSettings
  savedPaymentMethodId: string | undefined
  discountCode?: string
  customer?: { id: string }
  customerAuthToken?: string
  customData?: Record<string, unknown>
}

/**
 * What gets stored in localStorage during checkout flow.
 */
interface PaddleCheckoutDetails<T = PaddleCheckoutContent | PaddleCompactCheckoutContent> {
  id: string
  content: T
}

//
// Composable

export default function usePaddle() {
  const router = useRouter();
  const store = useLegacyStore();
  const { notifyError } = useNotify();

  function setCheckoutDetailsToStorage<T>(id: string, content: T): void {
    localStorage.setItem(
      "paddle_checkout_details",
      JSON.stringify({ id, content }),
    );
  }

  function paddleCheckoutSettings(
    content: PaddleBaseCheckoutContent,
    onePage = true,
  ): CheckoutSettings {
    return {
      locale: content.locale,
      showAddDiscounts: content.allow_discounts,
      variant: onePage ? "one-page" : "multi-page",
      allowedPaymentMethods: content.payment_methods ?? undefined,
      displayMode: "overlay",
    };
  }

  function buildCommonCheckoutData(
    content: PaddleBaseCheckoutContent,
    onePage = true,
  ): CommonCheckoutData {
    const isDiscountDefined: boolean = !!content.discount_code;
    const isCustomerDefined: boolean = !!content.customer && isObject(content.customer) && !isEmpty(content.customer);

    return {
      settings: paddleCheckoutSettings(content, onePage),
      savedPaymentMethodId: content.payment_method ?? undefined,
      ...(isDiscountDefined && { discountCode: content.discount_code }),
      ...(isCustomerDefined && { customer: { id: content.customer!.id } }),
      ...(isCustomerDefined && { customerAuthToken: content.customer!.auth_token }),
      ...(content.custom_data && { customData: content.custom_data }),
    };
  }

  function mapPriceItemsToLineItems(prices?: PaddlePriceItem[]): CheckoutOpenLineItem[] | undefined {
    if (!prices) return undefined;

    return prices.map((item): CheckoutOpenLineItem => ({
      priceId: item.price_id,
      quantity: item.quantity,
    }));
  }

  function buildPaddleCustomerId(): { id?: string } {
    const user = store?.getters?.user;

    if (!Array.isArray(user?.billing_customers)) {
      return {};
    }

    const billingInformation = user.billing_customers.find(
      (customer: { billing_type: number; billing_id?: string }) =>
        customer.billing_type === BILLING_TYPE.PADDLE,
    );

    if (billingInformation?.billing_id === undefined) {
      return {};
    }

    return { id: billingInformation.billing_id };
  }

  function handleCheckoutRedirect(
    transaction: string,
    query: Record<string, string>,
    organization: string | null = null,
  ): void {
    let redirectTo = "homepage";
    let params: Record<string, string> = {};

    if (
      transaction === PADDLE_TRANSACTION_TYPE.SUBSCRIPTION_PURCHASE ||
      transaction === PADDLE_TRANSACTION_TYPE.SUBSCRIPTION_PURCHASE_WITH_UNPAID_OVERAGE
    ) {
      redirectTo = "settings.subscription.success";
    } else if (transaction === PADDLE_TRANSACTION_TYPE.DOMAIN_PURCHASE) {
      if (isNil(organization)) {
        redirectTo = "shopping-cart.verify";
      } else {
        redirectTo = "org.shopping-cart.verify";
        params = { org: organization };
      }
    }

    router
      .push({
        name: redirectTo,
        params: params,
        query: query,
      })
      .then(() => {
        const paddle = getPaddleInstance();
        paddle?.Checkout?.close();
        localStorage.removeItem("paddle_checkout_details");
      });
  }

  function handlePaddleEvents(response: PaddleEventData): void {
    const detailsJson = localStorage.getItem("paddle_checkout_details");

    if (isNil(detailsJson) || isEmpty(detailsJson.trim())) {
      notifyError();
      logError("The \"paddle_checkout_details\" is missing or empty.");
      return;
    }

    const details: PaddleCheckoutDetails = JSON.parse(detailsJson);
    const eventData: CheckoutEventsData | undefined = response.data;

    switch (response.name) {
      case "checkout.closed": {
        if (details.id === PADDLE_TRANSACTION_TYPE.PAYMENT_METHOD_UPDATE) {
          XPaymentMethodStore().abortPaymentMethodUpdate();
        }

        XInvoiceStore().abortInvoicePayment();

        if (details.id === PADDLE_TRANSACTION_TYPE.INVOICE_PAYMENT) {
          router.replace({ name: "homepage" });
        }

        localStorage.removeItem("paddle_checkout_details");
        break;
      }

      case "checkout.completed": {
        logInfo(`Paddle checkout completed response: ${JSON.stringify(response)}`);

        if (
          details.id === PADDLE_TRANSACTION_TYPE.PAYMENT_METHOD_UPDATE ||
          details.id === PADDLE_TRANSACTION_TYPE.UNPAID_TRANSACTION_PAYMENT
        ) {
          if (details.id === PADDLE_TRANSACTION_TYPE.PAYMENT_METHOD_UPDATE) {
            XPaymentMethodStore().completePaymentMethodUpdate(response);
          }

          // Record the payment before "checkout.closed" fires, since closing
          // discards whatever is still pending.
          if (details.id === PADDLE_TRANSACTION_TYPE.UNPAID_TRANSACTION_PAYMENT) {
            XInvoiceStore().completeInvoicePayment();
          }

          const ACCEPTABLE_UX_DELAY_MS = 5000;

          setTimeout(() => {
            store?.dispatch("refreshState").then(() => {
              logInfo("State refreshed successfully!");
            });
          }, ACCEPTABLE_UX_DELAY_MS);

          return;
        }

        const txnId: string = eventData?.transaction_id ?? "";
        const content = details.content as PaddleCheckoutContent;
        const successUrl: string = content.success_url;
        const sessionUrl = successUrl.replace("{SESSION_ID}", txnId);
        const urlParams = new URLSearchParams(new URL(sessionUrl).search);
        const queryString = Object.fromEntries(urlParams.entries());
        handleCheckoutRedirect(details.id, queryString, content.organization ?? null);
        break;
      }

      case "checkout.error": {
        logError(`Paddle checkout failed. Response: ${JSON.stringify(response)}`);
        XInvoiceStore().abortInvoicePayment();
        break;
      }

      default: {
        break;
      }
    }
  }

  async function initPaddle(): Promise<void> {
    await initializePaddleFromPackage({
      environment: IS_PADDLE_LIVE === "false" ? "sandbox" : undefined,
      token: PADDLE_CLIENT_SECRET,
      pwCustomer: buildPaddleCustomerId(),
      checkout: {
        settings: {
          allowLogout: false,
          displayMode: "overlay",
          theme: "light",
          locale: "en",
        },
      },
      eventCallback: handlePaddleEvents,
    });
  }

  function updatePaddleCustomer(): void {
    const paddle = getPaddleInstance();

    if (!paddle?.Initialized) {
      return;
    }

    paddle.Update({ pwCustomer: buildPaddleCustomerId() });
  }

  function openPaddleCheckout(transaction: string, content: PaddleCheckoutContent): void {
    if (transaction === PADDLE_TRANSACTION_TYPE.SUBSCRIPTION_PURCHASE_WITH_UNPAID_OVERAGE) {
      openPaddleCheckoutWithUnpaidOverage(transaction, content);
      return;
    }

    setCheckoutDetailsToStorage(transaction, content);

    const paddle = getPaddleInstance();
    if (!paddle) return;

    const items: CheckoutOpenLineItem[] | undefined = mapPriceItemsToLineItems(content.prices);

    const options: CheckoutOpenOptions = {
      ...buildCommonCheckoutData(content, true),
      ...(items && { items }),
    } as CheckoutOpenOptions;

    paddle.Checkout.open(options);
  }

  function openPaddleCheckoutWithUnpaidOverage(
    transaction: string,
    content: PaddleCheckoutContent,
  ): void {
    setCheckoutDetailsToStorage(transaction, content);

    const paddle = getPaddleInstance();
    if (!paddle) return;

    const options: CheckoutOpenOptions = {
      ...buildCommonCheckoutData(content, true),
      transactionId: content.transaction_id,
    } as CheckoutOpenOptions;

    paddle.Checkout.open(options);
  }

  async function simulatePaddleSuccess(
    transaction: string,
    content: PaddleCheckoutContent,
  ): Promise<void> {
    const orderId: number = content.order_id;
    const orgSlug: string | undefined = content.organization;

    const url: string = orgSlug
      ? `/qa/app/api/organization/${orgSlug}/shopping-cart/order/${orderId}/simulate-payment`
      : `/qa/app/api/shopping-cart/order/${orderId}/simulate-payment`;

    try {
      await axios.post(url);
    } catch (e) {
      logError(`Failed to simulate payment: ${e}`);
    }

    const successUrl: string = content.success_url ?? "";
    const sessionMatch: RegExpMatchArray | null = successUrl.match(/session=([^&]+)/);
    const session: string = sessionMatch?.[1] ?? `mock-${orderId}`;

    const queryString: Record<string, string> = { session };
    handleCheckoutRedirect(transaction, queryString, orgSlug ?? null);
  }

  function openPaddleCheckoutByTransaction(
    transaction: string,
    content: PaddleCheckoutContent,
  ): void {
    setCheckoutDetailsToStorage(transaction, content);

    if (PADDLE_MOCK) {
      logInfo("Paddle mock mode enabled - simulating successful payment");
      simulatePaddleSuccess(transaction, content);
      return;
    }

    const paddle = getPaddleInstance();
    if (!paddle) return;

    const options: CheckoutOpenOptions = {
      transactionId: content.transaction_id,
      customerAuthToken: content.customer.auth_token,
      discountCode: content.discount_code,
      settings: paddleCheckoutSettings(content),
      savedPaymentMethodId: content.payment_method ?? undefined,
    } as CheckoutOpenOptions;

    paddle.Checkout.open(options);
  }

  /**
   * Replays the Paddle events a successful compact checkout would emit, so the mock
   * mode exercises the real `handlePaddleEvents` pipeline instead of duplicating it.
   */
  function simulateCompactPaddleSuccess(): void {
    handlePaddleEvents({ name: CheckoutEventNames.CHECKOUT_COMPLETED } as PaddleEventData);
    handlePaddleEvents({ name: CheckoutEventNames.CHECKOUT_CLOSED } as PaddleEventData);
  }

  function openPaddleCompactCheckoutByTransaction(
    transaction: string,
    content: PaddleCompactCheckoutContent,
  ): void {
    setCheckoutDetailsToStorage(transaction, content);

    if (
      PADDLE_MOCK && (
        transaction === PADDLE_TRANSACTION_TYPE.PAYMENT_METHOD_UPDATE ||
        transaction === PADDLE_TRANSACTION_TYPE.UNPAID_TRANSACTION_PAYMENT
      )
    ) {
      logInfo("Paddle mock mode enabled - simulating successful checkout");
      simulateCompactPaddleSuccess();

      return;
    }

    const paddle = getPaddleInstance();
    if (!paddle) return;

    const options: CheckoutOpenOptions = {
      ...buildCommonCheckoutData(content, false),
      transactionId: content.transaction_id,
    } as CheckoutOpenOptions;

    paddle.Checkout.open(options);
  }

  return {
    initPaddle,
    updatePaddleCustomer,
    openPaddleCheckout,
    openPaddleCheckoutByTransaction,
    openPaddleCompactCheckoutByTransaction,
  };
}
